<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <div class="element-box mt-4">
            <div class="row mt-4">
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>
              <CoachingButton
                  :enabled="companySettings?.caseManagementCoachingEnabled"
                  route-name="caseManagementCoachingNewAndActiveList"
                  coaching-type="caseManagement"
                  label="DRV Fallmanagement"/>
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <div class="row mt-5">
            <div class="col-lg-3 col-md-3 col-sm-12">
              <h6>Standortinformationen</h6>
              <div class="row mt-3">
                <div class="col">
                  <CardInfo :label="'Einzelcoaching'" :value="activeCoachingData?.counts?.privateCoaching"/>
                </div>
              </div>
            </div>

            <div class="col-lg-9 col-md-9 col-sm-12">
              <h6>{{ $t('dashboard.customer_information') }}</h6>
              <div class="row mt-3">
                <div class="col-md-4 col-sm-12">
                  <CardInfo :label="'Kunden insgesamt'" :value="totalCoaching"/>
                </div>
                <div class="col-md-4 col-sm-12">
                  <CardInfo :label="$t('general.active_customers')" :value="newCoachingData?.total"/>
                </div>
                <div class="col-md-4 col-sm-12">
                  <CardInfo :label="$t('general.active_customers')" :value="activeCoachingData?.total"/>
                </div>
              </div>
            </div>
          </div>

          <div class="element-box mt-4">
            <h6 class="mt-3"> Standortinformationen</h6>
            <div class="row mt-4">
              <div class="col-sm-12">
                <div class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                    <tr>
                      <th>Standort</th>
                      <th style="text-align: right">Aktive Kunden</th>
                      <th style="text-align: right">Neu Kunden</th>
                      <th style="text-align: right">geleistete Einheiten Februar</th>
                      <th style="text-align: right">geva</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="location of locationsData" v-bind:key="location?.locationName">
                      <td>
                        {{ location?.locationName }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.countActiveCoaching }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.countNewCoaching }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.unitsDone }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.countGeva }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts
              :key="seriesActiveCoaching.length"
              width="100%"
              type="donut"
              :options="chartOptionsActive"
              :series="seriesActiveCoaching"/>
        </div>

        <div class="element-box">
          <h6>{{ $t('general.new_customers') }}</h6>
          <VueApexCharts
              :key="seriesNewCoaching.length"
              width="100%"
              type="donut"
              :options="chartOptionsNew"
              :series="seriesNewCoaching"/>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardSiteSupportService from '@/api/Dashboard/SiteSupport';
import CardInfo from '@/components/card/CardInfo.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import {ErrorToast} from '@/components/VueToast';
import {optionsChartDonut} from '@/shared/utils/apex';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import {piniaUserStore} from '@/store/piniaUserStore';
import {computed, onMounted, ref} from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import {initAppIfAuthenticated} from "@/shared/functions";

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptionsActive = ref(optionsChartDonut);
const chartOptionsNew = ref(optionsChartDonut);
const seriesActiveCoaching = ref([]);
const seriesNewCoaching = ref([]);
const activeCoachingData = ref(null);
const newCoachingData = ref(null)
const locationsData = ref([])

async function getInfoData() {
  try {

    const {data} = await dashboardSiteSupportService.getInfoData();

    locationsData.value = data.data.locationsData;

    activeCoachingData.value = data.data?.activeCoachingData;
    seriesActiveCoaching.value = Object.values(activeCoachingData.value?.counts);

    chartOptionsActive.value = {
      ...chartOptionsActive.value,
      labels: [
        `Einzelcoaching ${activeCoachingData.value?.counts?.privateCoaching ?? 0}`,
        `Gruppenmaßnahmen ${activeCoachingData.value?.counts?.groupCoaching ?? 0}`,
        `Soziotherapie ${activeCoachingData.value?.counts?.socioTherapyCoaching ?? 0}`,
        `Private Weiterbildung ${activeCoachingData.value?.counts?.privateLearningCoaching ?? 0}`,
        `Betreutes Wohnen ${activeCoachingData.value?.counts?.assistedLivingCoaching ?? 0}`,
        `DRV Fallmanagement ${activeCoachingData.value?.counts?.caseManagementCoaching ?? 0}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                formatter: () => activeCoachingData.value?.total.toString(),
              },
            },
          },
        },
      }
    };

    newCoachingData.value = data.data?.newCoachingData;
    seriesNewCoaching.value = Object.values(newCoachingData.value?.counts);

    chartOptionsNew.value = {
      ...chartOptionsNew.value,
      labels: [
        `Einzelcoaching ${newCoachingData.value?.counts?.privateCoaching ?? 0}`,
        `Gruppenmaßnahmen ${newCoachingData.value?.counts?.groupCoaching ?? 0}`,
        `Soziotherapie ${newCoachingData.value?.counts?.socioTherapyCoaching ?? 0}`,
        `Private Weiterbildung ${newCoachingData.value?.counts?.privateLearningCoaching ?? 0}`,
        `Betreutes Wohnen ${newCoachingData.value?.counts?.assistedLivingCoaching ?? 0}`,
        `DRV Fallmanagement ${newCoachingData.value?.counts?.caseManagementCoaching ?? 0}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                formatter: () => newCoachingData.value?.total.toString(),
              },
            },
          },
        },
      }
    };

  } catch (error) {
    await ErrorToast(error);
  }
}

const totalCoaching = computed(() => {
  return (newCoachingData.value?.total ?? 0) + (activeCoachingData.value?.total ?? 0);
});


onMounted(async () => {
  await initAppIfAuthenticated()
  await getInfoData();
});
</script>

<style scoped>

::v-deep(.apexcharts-legend) {
  display: block !important;
}

</style>